import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Chip,
} from '@material-ui/core';
import { Edit, Delete } from '@material-ui/icons';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { AlertCard, AlertCardBody, getAlertCardColors } from '../../../../../common/cards/alert-card/AlertCard';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableContainer: {
      marginTop: '1rem',
      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
      border: `1px solid ${theme.color.grey.border}`,
      borderRadius: '8px',
      overflow: 'hidden',
    },
    tableHeader: {
      // No background color - matches reminders pattern
    },
    tableHeaderCell: {
      fontWeight: 600,
      fontSize: '12px',
      color: theme.color.grey.neutralBrand500,
      borderBottom: `1px solid ${theme.color.grey.border}`,
      padding: '16px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    tableRow: {
      '&:hover': {
        backgroundColor: theme.color.grey.neutralBrand50,
      },
      borderBottom: `1px solid ${theme.color.grey.border}`,
    },
    tableCell: {
      borderBottom: 'none',
      padding: '16px',
      fontSize: '14px',
      verticalAlign: 'middle',
    },
    actionButtons: {
      display: 'flex',
      gap: '4px',
      justifyContent: 'flex-end',
    },
    actionButton: {
      padding: '4px',
      '&:hover': {
        backgroundColor: theme.color.grey.neutralBrand100,
      },
    },
    editButton: {
      color: theme.color.grey.neutralBrand500,
    },
    deleteButton: {
      color: theme.color.grey.neutralBrand500,
    },
    statusChip: {
      fontSize: '11px',
      height: '22px',
      minWidth: '60px',
    },
    enabledChip: {
      backgroundColor: theme.status.green.light,
      color: 'white',
      fontWeight: 600,
      textTransform: 'uppercase',
      borderRadius: 5,
    },
    disabledChip: {
      backgroundColor: theme.color.grey.neutralBrand100,
      color: theme.color.grey.neutralBrand500,
      fontWeight: 600,
      textTransform: 'uppercase',
      borderRadius: 5,
    },
    notificationTypeText: {
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    notificationTypeContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    typeIndicator: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      flexShrink: 0,
    },
    rosterIndicator: {
      backgroundColor: theme.alert.blue.light, // Blue for roster-related
    },
    applicationIndicator: {
      backgroundColor: '#00695C', // Teal for application-related
    },
    volunteerIndicator: {
      backgroundColor: '#7B1FA2', // Purple for volunteer-related
    },
    reportIndicator: {
      backgroundColor: '#F57C00', // Orange for report-related
    },
    incidentIndicator: {
      backgroundColor: '#D32F2F', // Red for incident-related
    },
    taskIndicator: {
      backgroundColor: '#388E3C', // Green for task-related
    },
    notificationTitle: {
      fontWeight: 500,
      marginBottom: '4px',
    },
    notificationDescription: {
      fontSize: '12px',
      color: '#808080',
    },
    actionsCell: {
      width: '120px',
    },
    statusCell: {
      width: '100px',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: '#808080',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      padding: '40px',
    },
  })
);

const getNotificationTypeLabel = (type: string): string => {
  const typeMap: Record<string, string> = {
    'flexible-roster-template-created': 'Roster Template - Date Added',
    'roster-cancellation': 'Roster Cancellation',
    'application-submitted': 'New Incoming Application',
    'volunteer-declined-rostering': 'Attendance Reminder Response - "No"',
    'volunteer-accepted-rostering': 'Attendance Reminder Response - "Yes"',
    'volunteer-unavailability-updated': 'Unavailability Period - All Submissions',
    'volunteer-unavailability-conflict': 'Unavailability Period - Conflicts Only',
    'ad-hoc-report-submitted': 'Ad-Hoc Report Submitted',
    // New manager notification types
    'new-incident-report': 'New Incident Report',
    'new-incident-report-comment': 'New Incident Report Comment',
    'activity-report-created': 'Activity Report Created',
    'activity-unavailability-submitted': 'Unavailability Date Submitted',
    'activity-availability-submitted': 'Availability Submitted',
    'task-expired-manager-alert': 'Task Expired Manager Alert',
    'new-activity-application': 'New Activity Application',
    'new-activity-waitlisting': 'New Activity Waitlisting',
    'new-activity-enrolment-manager-alert': 'New Activity Enrolment',
    'volunteer-self-exited': 'Volunteer Self Exited',
    'onboarding-tasks-complete': 'Onboarding Tasks Complete',
    'role-application-submitted': 'New Role Application',
    'role-application-tasks-completed': 'Role Application - Tasks Completed',
    'role-application-withdrawn': 'Role Application - Withdrawn',
  };
  return typeMap[type] || type;
};

const getRecipientTypeLabel = (config: NotificationConfig): string => {
  const recipients: string[] = [];

  if (config.notifyAdmins) recipients.push('Admins');
  if (config.notifyProgramManagers) recipients.push('Program Managers');
  if (config.notifyActivityLeaders) recipients.push('Activity Leaders');
  if (config.notifySpecificManagers && config.managers && config.managers.length > 0) {
    recipients.push(`${config.managers.length} Manager${config.managers.length > 1 ? 's' : ''}`);
  }
  if (config.customEmail) recipients.push('Custom Email');

  return recipients.length > 0 ? recipients.join(', ') : 'No Recipients';
};

const getTypeIndicatorClass = (type: string, classes: any): string => {
  switch (type) {
    case 'flexible-roster-template-created':
    case 'roster-cancellation':
    case 'volunteer-unavailability-updated':
    case 'volunteer-unavailability-conflict':
    case 'activity-unavailability-submitted':
    case 'activity-availability-submitted':
      return `${classes.typeIndicator} ${classes.rosterIndicator}`;
    case 'application-submitted':
    case 'new-activity-application':
    case 'new-activity-waitlisting':
    case 'new-activity-enrolment-manager-alert':
    case 'onboarding-tasks-complete':
    case 'role-application-submitted':
    case 'role-application-tasks-completed':
    case 'role-application-withdrawn':
      return `${classes.typeIndicator} ${classes.applicationIndicator}`;
    case 'volunteer-declined-rostering':
    case 'volunteer-accepted-rostering':
    case 'volunteer-self-exited':
      return `${classes.typeIndicator} ${classes.volunteerIndicator}`;
    case 'ad-hoc-report-submitted':
    case 'activity-report-created':
      return `${classes.typeIndicator} ${classes.reportIndicator}`;
    case 'new-incident-report':
    case 'new-incident-report-comment':
      return `${classes.typeIndicator} ${classes.incidentIndicator}`;
    case 'task-expired-manager-alert':
      return `${classes.typeIndicator} ${classes.taskIndicator}`;
    default:
      return `${classes.typeIndicator} ${classes.rosterIndicator}`;
  }
};

interface Manager {
  managerId: string;
  profile: {
    profileId: string;
    preferredName: string;
    lastName: string;
    email: string | null;
    avatarUrl: string | null;
  };
}

export interface NotificationConfig {
  notificationConfigId: string;
  notificationType: string;
  notifyAdmins: boolean;
  notifyProgramManagers: boolean;
  notifyActivityLeaders: boolean;
  notifySpecificManagers: boolean;
  customEmail?: string | null;
  isEnabled: boolean;
  managers?: Manager[];
}

interface NotificationConfigTableProps {
  notificationConfigs: NotificationConfig[];
  onEdit: (config: NotificationConfig) => void;
  onDelete: (configId: string) => void;
  loading?: boolean;
  error?: any;
}

const NotificationConfigTable: React.FC<NotificationConfigTableProps> = ({
  notificationConfigs,
  onEdit,
  onDelete,
  loading,
  error,
}) => {
  const classes = useStyles();
  const { lightTextColor } = getAlertCardColors('urgent');

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box style={{ marginTop: '1rem' }}>
        <AlertCard variant='urgent' title='Error!'>
          <AlertCardBody>
            <Typography variant='subtitle2' style={{ fontSize: '14px', fontWeight: 400, color: lightTextColor }}>
              Volaby has experienced an error. {error.message || 'Unable to load notification configurations'}
            </Typography>
          </AlertCardBody>
        </AlertCard>
      </Box>
    );
  }

  if (notificationConfigs.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant='body2'>
          No notifications found. Click &quot;Add Notification&quot; to create your first notification configuration.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table>
        <TableHead className={classes.tableHeader}>
          <TableRow>
            <TableCell className={classes.tableHeaderCell}>Type</TableCell>
            <TableCell className={classes.tableHeaderCell}>Recipients</TableCell>
            <TableCell className={classes.tableHeaderCell}>Status</TableCell>
            <TableCell className={classes.tableHeaderCell} align='center'>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {notificationConfigs.map((config) => (
            <TableRow key={config.notificationConfigId} className={classes.tableRow}>
              <TableCell className={classes.tableCell}>
                <Box className={classes.notificationTypeContainer}>
                  <Box className={getTypeIndicatorClass(config.notificationType, classes)} />
                  <Typography className={classes.notificationTypeText}>
                    {getNotificationTypeLabel(config.notificationType)}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell className={classes.tableCell}>{getRecipientTypeLabel(config)}</TableCell>
              <TableCell className={classes.tableCell}>
                <Chip
                  label={config.isEnabled ? 'Enabled' : 'Disabled'}
                  className={`${classes.statusChip} ${config.isEnabled ? classes.enabledChip : classes.disabledChip}`}
                  size='small'
                />
              </TableCell>
              <TableCell className={classes.tableCell} align='center'>
                <Box className={classes.actionButtons}>
                  <IconButton
                    className={`${classes.actionButton} ${classes.editButton}`}
                    onClick={() => onEdit(config)}
                    title='Edit notification configuration'
                  >
                    <Edit fontSize='small' />
                  </IconButton>
                  <IconButton
                    className={`${classes.actionButton} ${classes.deleteButton}`}
                    onClick={() => onDelete(config.notificationConfigId)}
                    title='Delete notification configuration'
                  >
                    <Delete fontSize='small' />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export { NotificationConfigTable };
